/**
 * FeatureRequests – Wunschliste / Aenderungswuensche
 * Extrahiert aus AdminPage.jsx
 */
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X, Lightbulb, Bug, Palette, Settings, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RefreshCw } from 'lucide-react';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const PRIO_COLORS = {
  hoch: 'bg-red-500 text-white',
  mittel: 'bg-orange-400 text-white',
  niedrig: 'bg-green-500 text-white',
};
const KAT_CONFIG = {
  feature: { label: 'Feature', icon: Lightbulb, color: 'text-blue-600' },
  bugfix: { label: 'Bugfix', icon: Bug, color: 'text-red-600' },
  design: { label: 'Design', icon: Palette, color: 'text-purple-600' },
  sonstiges: { label: 'Sonstiges', icon: Settings, color: 'text-slate-600' },
};
const STATUS_MAP = {
  offen: 'bg-amber-100 text-amber-700',
  in_arbeit: 'bg-blue-100 text-blue-700',
  erledigt: 'bg-green-100 text-green-700',
  abgelehnt: 'bg-slate-100 text-slate-500',
};

function FeatureRequestForm({ open, editData, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titel: '', beschreibung: '', prioritaet: 'mittel', kategorie: 'feature' });

  useEffect(() => {
    if (open) {
      setForm(editData ? {
        titel: editData.titel || '', beschreibung: editData.beschreibung || '',
        prioritaet: editData.prioritaet || 'mittel', kategorie: editData.kategorie || 'feature',
      } : { titel: '', beschreibung: '', prioritaet: 'mittel', kategorie: 'feature' });
    }
  }, [open, editData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titel.trim()) { toast.error('Bitte einen Titel eingeben'); return; }
    setSaving(true);
    try {
      if (editData) {
        await betreuungAPI.updateFeatureRequest(editData.id, form);
        toast.success('Aktualisiert');
      } else {
        await betreuungAPI.createFeatureRequest(form);
        toast.success('Aenderungswunsch angelegt');
      }
      onSaved();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <Card className="max-w-lg mx-auto mt-4 border-slate-300 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{editData ? 'Wunsch bearbeiten' : 'Neuer Aenderungswunsch'}</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <CardDescription>Beschreiben Sie was geaendert oder hinzugefuegt werden soll</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Titel *</Label>
            <Input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))}
              placeholder="z.B. Timeline-Redesign auf der Aufgabenseite" data-testid="feature-titel" />
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Textarea value={form.beschreibung} onChange={e => setForm(p => ({ ...p, beschreibung: e.target.value }))}
              rows={4} placeholder="Genauere Beschreibung..." data-testid="feature-beschreibung" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kategorie</Label>
              <Select value={form.kategorie} onValueChange={v => setForm(p => ({ ...p, kategorie: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="bugfix">Bugfix</SelectItem>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioritaet</Label>
              <Select value={form.prioritaet} onValueChange={v => setForm(p => ({ ...p, prioritaet: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoch">Hoch</SelectItem>
                  <SelectItem value="mittel">Mittel</SelectItem>
                  <SelectItem value="niedrig">Niedrig</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Wird gespeichert...' : editData ? 'Speichern' : 'Anlegen'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default function FeatureRequestList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filter, setFilter] = useState('offen');

  const loadData = async () => {
    setLoading(true);
    try {
      const params = filter && filter !== 'alle' ? { status: filter } : {};
      const data = await betreuungAPI.listFeatureRequests(params);
      setItems(Array.isArray(data) ? data : []);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich loeschen?')) return;
    await betreuungAPI.deleteFeatureRequest(id);
    toast.success('Geloescht');
    loadData();
  };

  const handleStatusChange = async (id, status) => {
    await betreuungAPI.updateFeatureRequest(id, { status });
    toast.success('Status aktualisiert');
    loadData();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Sammeln Sie hier Aenderungswuensche und Feature-Ideen.
        </p>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle</SelectItem>
              <SelectItem value="offen">Offen</SelectItem>
              <SelectItem value="in_arbeit">In Arbeit</SelectItem>
              <SelectItem value="erledigt">Erledigt</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditItem(null); setDialogOpen(true); }} data-testid="new-feature-btn">
            <Plus className="w-4 h-4 mr-2" /> Neuer Wunsch
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length > 0 ? (
        <div className="space-y-2">
          {items.map(item => {
            const kat = KAT_CONFIG[item.kategorie] || KAT_CONFIG.sonstiges;
            const KatIcon = kat.icon;
            return (
              <div key={item.id} className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`feature-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <KatIcon className={`w-5 h-5 mt-0.5 shrink-0 ${kat.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-800">{item.titel}</p>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIO_COLORS[item.prioritaet] || PRIO_COLORS.mittel}`}>
                          {item.prioritaet}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${STATUS_MAP[item.status] || STATUS_MAP.offen}`}>
                          {item.status === 'in_arbeit' ? 'In Arbeit' : item.status}
                        </span>
                      </div>
                      {item.beschreibung && <p className="text-sm text-slate-500 mt-1">{item.beschreibung}</p>}
                      <p className="text-xs text-slate-400 mt-1">{new Date(item.created_date).toLocaleDateString('de-DE')}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                        <Edit className="w-4 h-4 mr-2" /> Bearbeiten
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'erledigt')}>
                        <Check className="w-4 h-4 mr-2" /> Als erledigt markieren
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4 mr-2" /> Loeschen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
          <Lightbulb className="w-14 h-14 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-2">Noch keine Aenderungswuensche</p>
          <p className="text-sm text-slate-400 mb-4">Sammeln Sie hier Ideen fuer den naechsten Entwicklungs-Sprint</p>
          <Button onClick={() => { setEditItem(null); setDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Ersten Wunsch anlegen
          </Button>
        </div>
      )}

      <FeatureRequestForm
        open={dialogOpen}
        editData={editItem}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); loadData(); }}
      />
    </div>
  );
}
